import type { MerchantTaxonomyEntry, ShopifyFieldOwner, StoreLayoutSettings } from '../../../services/integrationApi';
import {
  FallbackMode,
  fallbackLabel,
  fallbackModeOf,
  ProductTypeRule,
  resolveTaxonomyForSlug,
  taxonomyLabel,
  taxonomyShortLabel,
  unmappedCategoryIsUnset,
  unmappedFallbackGid,
} from '../mappingModel';

export type TraceStatus = 'decided' | 'skipped' | 'not_reached';

export interface TraceRow {
  /** 1 taxonomy, 2 name rules, 3 category map, 4 fallback; 0 is the per-product override row. */
  step: 0 | 1 | 2 | 3 | 4;
  label: string;
  status: TraceStatus;
  note: string;
}

export interface TryResult {
  type: string;
  typeVia: string;
  category: string;
  categoryVia: string;
  /** False when the type came from a fallback that is not a deliberate choice (derive, flag, no default typed). */
  typeOk: boolean;
  trace: TraceRow[];
}

export interface TryInput {
  name: string;
  sku: string;
  category: string;
  layout: StoreLayoutSettings | undefined;
  /** The registry entry the store SKU resolved to; null when none matched. */
  registryEntry: MerchantTaxonomyEntry | null;
  /** True when the store has a registry at all, so "no entry for this SKU" can be told from "no taxonomy". */
  hasRegistry: boolean;
  /** Flag `name_rule_shopify_category`: a rule outranks the category map and may set the category. */
  nameRuleCategory: boolean;
  categoryDefaults: { defaults: Record<string, string | null>; neutralGid: string } | undefined;
  options: Array<{ gid: string; label: string }>;
  categoryOwner: ShopifyFieldOwner | undefined;
  typeOwner: ShopifyFieldOwner | undefined;
  /** A Shopify category set on this one product (`productCategoryOverrides`); only passed when the product is known. */
  productOverrideGid?: string;
  /** Flag `shopify_unmapped_category_unset`: a stored "Don't set" fallback leaves the category alone. */
  unmappedCategoryUnset?: boolean;
}

/** Same match as the server's `matchProductTypeRule`: any non-blank keyword found in the name. */
export function matchRule(name: string, rules: ProductTypeRule[]): { rule: ProductTypeRule; keyword: string } | null {
  const probe = name.toLowerCase();
  for (const rule of rules) {
    const keyword = rule.keywords.find((kw) => kw.trim() && probe.includes(kw.trim().toLowerCase()));
    if (keyword) return { rule, keyword: keyword.trim() };
  }
  return null;
}

/**
 * What the next sync would write for one product, and which step decided each field.
 * Type: taxonomy (store SKU) -> name rule -> category map -> fallback; with the
 * `name_rule_shopify_category` flag off the server runs the map before the rules, and so does this.
 * Category: taxonomy -> per-product override -> name rule category (flag) -> map override ->
 * Motzu default for the slug -> the store's fallback / neutral category.
 */
export function resolveTry(input: TryInput): TryResult {
  const { layout, name } = input;
  const slug = input.category.trim();
  const rules = layout?.productTypeRules || [];
  const mapOn = layout?.useDiscoveredMapping !== false;
  const categoryMap = layout?.categoryMap || {};
  const mode: FallbackMode = fallbackModeOf(layout);
  const entry = input.registryEntry;
  const entryType = entry?.productType?.trim() || '';
  const entryGid = entry?.shopifyCategoryGid || '';
  const hit = matchRule(name, rules);
  const mapped = (categoryMap[slug] || categoryMap[slug.toLowerCase()] || '').trim();

  let typeStep: 1 | 2 | 3 | 4 = 4;
  let type = '';
  let typeVia = 'Unmatched Product Rules';
  const viaRule = () => {
    if (hit?.rule.productType.trim()) {
      typeStep = 2;
      type = hit.rule.productType.trim();
      typeVia = `Name Rule "${hit.keyword}"`;
      return true;
    }
    return false;
  };
  const viaMap = () => {
    if (mapOn && mapped) {
      typeStep = 3;
      type = mapped;
      typeVia = 'Motzu Classification Override';
      return true;
    }
    return false;
  };
  if (entryType) {
    typeStep = 1;
    type = entryType;
    typeVia = `Store Taxonomy ${entry?.skuPrefix || entry?.subcategoryCode || ''}`.trim();
  } else if (input.nameRuleCategory ? viaRule() || viaMap() : viaMap() || viaRule()) {
    // decided above
  } else {
    type = fallbackLabel(slug, mode, layout?.defaultProductType);
  }
  const typeOk = typeStep < 4 || (mode === 'default' && Boolean(layout?.defaultProductType?.trim()));

  let catStep: 0 | 1 | 2 | 3 | 4 = 4;
  let catGid = '';
  let categoryVia = 'Unmatched Product Rules';
  // "Don't set" (flag `shopify_unmapped_category_unset`): the sync sends no category, so Shopify keeps its own.
  let fallbackUnset = false;
  const ruleGid = input.nameRuleCategory ? hit?.rule.taxonomyCategoryGid : undefined;
  if (entryGid) {
    catStep = 1;
    catGid = entryGid;
    categoryVia = 'Store Taxonomy';
  } else if (input.productOverrideGid) {
    catStep = 0;
    catGid = input.productOverrideGid;
    categoryVia = 'Per-product override';
  } else if (ruleGid) {
    catStep = 2;
    catGid = ruleGid;
    categoryVia = 'Name Rule';
  } else {
    const resolved = slug ? resolveTaxonomyForSlug(slug, layout, input.categoryDefaults) : null;
    const neutral = input.categoryDefaults?.neutralGid || '';
    if (resolved && (resolved.step === 'store' || resolved.step === 'default')) {
      catStep = 3;
      catGid = resolved.gid;
      categoryVia = resolved.step === 'store' ? 'Motzu Classification Override' : 'Motzu default';
    } else if (unmappedCategoryIsUnset(layout, input.unmappedCategoryUnset === true)) {
      catGid = '';
      fallbackUnset = true;
      categoryVia = "Unmatched Product Rules · Don't set";
    } else {
      catGid = unmappedFallbackGid(layout) || neutral;
    }
  }
  const categoryName = fallbackUnset
    ? 'Left as is'
    : catGid ? taxonomyShortLabel(taxonomyLabel(input.options, catGid)) : 'Cameras & Optics';

  const typeLeftAlone = input.typeOwner === 'never';
  const catLeftAlone = input.categoryOwner === 'never';

  const notes: string[] = [
    !input.hasRegistry
      ? 'no Store Taxonomy uploaded'
      : entry ? `matched ${entry.skuPrefix || entry.subcategoryCode || entry.id}` : 'no entry for this SKU',
    hit ? `matched "${hit.keyword}"` : 'no keyword in the name',
    !mapOn
      ? 'overrides switched off'
      : mapped ? `type ${mapped}` : slug ? `no type for ${slug}` : 'no category given',
    'used for anything left',
  ];
  const decidedSteps = new Set<number>([typeStep, catStep]);
  const last = Math.max(typeStep, catStep);
  const rows: TraceRow[] = [];
  if (catStep === 0) {
    rows.push({ step: 0, label: 'Per-product override', status: 'decided', note: 'set on this product · decides category' });
  }
  (['Store Taxonomy', 'Name Rules', 'Motzu Classification Overrides', 'Unmatched Product Rules'] as const).forEach((label, i) => {
    const step = (i + 1) as 1 | 2 | 3 | 4;
    const decides = decidedSteps.has(step);
    const both = typeStep === step && catStep === step;
    const suffix = decides ? (both ? ' · decides both' : typeStep === step ? ' · decides type' : ' · decides category') : '';
    rows.push({
      step,
      label,
      status: step > last ? 'not_reached' : decides ? 'decided' : 'skipped',
      note: step > last ? 'not reached' : `${notes[i]}${suffix}`,
    });
  });

  return {
    type: typeLeftAlone ? 'Left as is' : type,
    typeVia: typeLeftAlone ? 'Never touch' : typeVia,
    category: catLeftAlone ? 'Left as is' : categoryName,
    categoryVia: catLeftAlone ? 'Never touch' : categoryVia,
    typeOk: typeLeftAlone ? true : typeOk,
    trace: rows,
  };
}
